import { getEnvVariable } from "../packages/base/util/environment.js";
import { config } from "../config/index.js";
import { jwApi } from "../api/index.js";
import { getTypedUploadManager } from "../packages/base/upload/manager.js";
import { UploadDirect } from "../packages/base/upload/methods/direct.js";
import { UploadExternal } from "../packages/base/upload/methods/external.js";
import { UploadFetch } from "../packages/base/upload/methods/fetch.js";
import { UploadMultiPart } from "../packages/base/upload/methods/multipart.js";
import { cleanFilename } from "../packages/base/upload/util.js";

//#region src/upload/index.ts
const uploadManager = getTypedUploadManager().getPublicApi();
const getUploadMetadata = (params) => {
	function cleanFilename$1(filename) {
		if ("assertTitleFromFilename" in params.upload && params.upload.assertTitleFromFilename === "original") return filename;
		return cleanFilename(filename);
	}
	return {
		titleFromFilename: (() => {
			if ("file" in params.upload) return cleanFilename$1(params.upload.file.name);
			if ("externalUrl" in params.upload) return cleanFilename$1(params.upload.externalUrl.split("/").pop());
			if ("fetchUrl" in params.upload) return cleanFilename$1(params.upload.fetchUrl.split("/").pop());
			return "Unknown";
		})(),
		uploadMethod: (() => {
			if ("file" in params.upload) {
				const maxDirectSize = config.get().upload.direct.maxSize;
				if (params.upload.file.size > maxDirectSize) return "multipart";
				return "direct";
			}
			if ("externalUrl" in params.upload) return "external";
			if ("fetchUrl" in params.upload) return "fetch";
			throw new Error("Could not determine upload method!");
		})()
	};
};
async function jwUpload(type, params, opts) {
	if (getEnvVariable("TEST") && typeof opts?.pollAsset === "undefined") opts = {
		...opts,
		pollAsset: false
	};
	const conf = (await (await import("./conf.js")).default[type]()).default;
	const metadata = getUploadMetadata(params);
	function getIdentifier(id) {
		return {
			id,
			routeParams: params.routeParams
		};
	}
	async function createAsset() {
		return conf.createAsset(params, metadata, opts);
	}
	async function readAsset(id) {
		return conf.readAsset(id, params.routeParams, opts);
	}
	const pollAssetCfg = opts?.pollAsset === false ? void 0 : conf.pollAsset ? {
		...conf.pollAsset,
		...opts?.pollAsset
	} : void 0;
	switch (metadata.uploadMethod) {
		case "direct": {
			if (!("file" in params.upload)) throw new Error("Missing File to upload!");
			const file = params.upload.file;
			return new UploadDirect({
				type,
				metadata,
				file,
				createAsset,
				readAsset,
				pollAssetCfg,
				getIdentifier,
				debug: opts?.debug,
				mimeType: file.type
			});
		}
		case "multipart": {
			if (!("file" in params.upload)) throw new Error("Missing File to upload!");
			const file = params.upload.file;
			return new UploadMultiPart({
				type,
				metadata,
				file,
				createAsset,
				readAsset,
				pollAssetCfg,
				getIdentifier,
				debug: opts?.debug,
				getParts: async (params$1, opts$1) => {
					const uploadId = params$1.asset.upload_id;
					if (!uploadId) throw new Error("Asset does not have upload_id!");
					const uploadToken = params$1.asset.upload_token;
					if (!uploadToken) throw new Error("Asset does not have upload_token!");
					return (await jwApi("uploads", "getParts", {
						routeParams: { uploadId },
						headers: { Authorization: `Bearer ${uploadToken}` },
						queryParams: {
							page: params$1.page,
							page_length: params$1.pageLength
						}
					}, opts$1)).data.parts;
				},
				completeUpload: async (params$1, opts$1) => {
					const uploadId = params$1.asset.upload_id;
					if (!uploadId) throw new Error("Asset does not have upload_id!");
					const uploadToken = params$1.asset.upload_token;
					if (!uploadToken) throw new Error("Asset does not have upload_token!");
					await jwApi("uploads", "complete", {
						routeParams: { uploadId },
						headers: { Authorization: `Bearer ${uploadToken}` }
					}, opts$1);
				}
			}, { multiPartOptions: config.get().upload.multipart });
		}
		case "external":
			if (!("externalUrl" in params.upload)) throw new Error("Undefined params.upload.externalUrl");
			return new UploadExternal({
				type,
				metadata,
				url: params.upload.externalUrl,
				createAsset,
				readAsset,
				pollAssetCfg,
				getIdentifier,
				debug: opts?.debug
			});
		case "fetch":
			if (!("fetchUrl" in params.upload)) throw new Error("Undefined params.upload.fetchUrl");
			return new UploadFetch({
				type,
				metadata,
				url: params.upload.fetchUrl,
				createAsset,
				readAsset,
				pollAssetCfg,
				getIdentifier,
				debug: opts?.debug
			});
	}
}

//#endregion
export { jwUpload, uploadManager };